import type {
  BenchRoomRef,
  CourtOfficer,
  CourtOfficerCourtType,
  CourtOfficerFormState,
  CourtOfficerRole,
  CourtOfficerStatus,
  CourtRoomRef,
  HighCourtRef,
  DistrictRef,
  ComplexRef,
} from "./types";

export const HC_BLUE = "#2563EB";
export const DC_ORANGE = "#f26422";

export const emptyOfficerForm: CourtOfficerFormState = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  username: "",
  role: "",
  courtType: "high_courts",
  status: "Active",
  assignmentId: "",
  highCourtId: "",
  benchId: "",
  courtrooms: [],
  stateName: "",
  districtId: "",
  complexId: "",
  password: "",
};

export const courtTypeLabel = (courtType?: string) =>
  courtType === "district_courts" ? "District Court" : "High Court";

export const contextTitle = (courtType: CourtOfficerCourtType) =>
  courtType === "district_courts"
    ? "District Court Officers"
    : "High Court Officers";

export const officerId = (officer: CourtOfficer) =>
  String(officer._id || officer.id || "");

export const officerName = (officer: CourtOfficer) =>
  `${officer.firstName || ""} ${officer.lastName || ""}`.trim() ||
  officer.username ||
  "Unnamed Officer";

export const initials = (officer: CourtOfficer) => {
  const name = officerName(officer);
  const [first = "", second = ""] = name.split(" ");
  return `${first[0] || ""}${second[0] || ""}`.toUpperCase() || "O";
};

export const refId = (value: unknown) => {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (typeof value === "object") {
    const ref = value as { _id?: string; id?: string };
    return String(ref._id || ref.id || "");
  }
  return "";
};

export const getOfficerAssignmentId = (officer: CourtOfficer) => {
  if (officer.courtType === "district_courts") return refId(officer.courtRoom);
  return refId(officer.benchRoom || officer.bench);
};

export const getOfficerHighCourtId = (officer: CourtOfficer) =>
  refId(officer.highcourt);

export const benchRoomLabel = (room: BenchRoomRef) => {
  const name = room.benchName || "Bench Room";
  const courtRoom = room.courtRoom ? ` · ${room.courtRoom}` : "";
  return `${name}${courtRoom}`;
};

export const courtRoomLabel = (room: CourtRoomRef) => {
  const name = room.courtRoomName || room.name || "Courtroom";
  const roomNo = room.roomNumber ? ` · ${room.roomNumber}` : "";
  return `${name}${roomNo}`;
};

export const highCourtLabel = (court: HighCourtRef) =>
  court.highCourtName || court.stateName || "High Court";

export const districtLabel = (district: DistrictRef) => {
  const name = district.districtName || "District Court";
  const state = district.stateName ? `, ${district.stateName}` : "";
  return `District Court, ${name}${state}`;
};

export const complexLabel = (complex: ComplexRef) =>
  complex.complexName || complex.address || "Court Complex";

export const roleLabel = (
  role?: string,
  courtType?: CourtOfficerCourtType | string,
) => {
  if (role === "Judge") {
    return courtType === "district_courts"
      ? "District Judge"
      : "High Court Judge";
  }
  return role || "Officer";
};

export const isAssignableRole = (role?: string) =>
  role === "Reader" || role === "Controller";

export const normalizeStatus = (status?: string): CourtOfficerStatus =>
  status === "Inactive" ? "Inactive" : "Active";

export const normalizeCourtType = (
  courtType?: string,
): CourtOfficerCourtType =>
  courtType === "district_courts" ? "district_courts" : "high_courts";

export const normalizeRole = (role?: string): CourtOfficerRole | "" => {
  if (role === "Judge" || role === "Reader" || role === "Controller") {
    return role;
  }
  return "";
};

export const toOfficerForm = (
  officer?: CourtOfficer,
): CourtOfficerFormState => {
  if (!officer) return emptyOfficerForm;
  return {
    firstName: officer.firstName || "",
    lastName: officer.lastName || "",
    email: officer.email || "",
    phone: officer.phone || "",
    username: officer.username || "",
    role: normalizeRole(officer.role),
    courtType: normalizeCourtType(officer.courtType),
    status: normalizeStatus(officer.status),
    assignmentId: getOfficerAssignmentId(officer),
    highCourtId: getOfficerHighCourtId(officer),
    courtrooms: officer.courtrooms || [],
    benchId: refId(officer.benchRoom),
    stateName:
      typeof officer.district === "object" && officer.district
        ? officer.district.stateName || ""
        : "",
    districtId: refId(officer.district),
    complexId: refId(officer.complex),
    password: "",
  };
};

export const generateTemporaryPassword = () => {
  const upper = "ABCDEFGHJKLMNPQRSTUVWXYZ";
  const lower = "abcdefghijkmnopqrstuvwxyz";
  const digits = "23456789";
  const symbols = "@$!%*?&";
  const all = `${upper}${lower}${digits}${symbols}`;
  const pick = (pool: string) => pool[Math.floor(Math.random() * pool.length)];
  return [
    pick(upper),
    pick(lower),
    pick(digits),
    pick(symbols),
    ...Array.from({ length: 8 }, () => pick(all)),
  ]
    .sort(() => Math.random() - 0.5)
    .join("");
};
